import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FiMove, FiChevronRight, FiChevronDown, FiTag, FiShield, FiX, FiCornerUpLeft, FiCheck } from 'react-icons/fi';

// =============================================
// ServiceBoard — the drag-and-drop layout for the Relationship Workbench.
//   • Left: the service tree (auto-expanded). Drag a service onto another to
//     re-parent it, or onto the "Top level" zone to un-nest it.
//   • Right: columns — toggle between PLANS and ROLES.
//       Plans  → drop a service to include it; a popover sets access level + item
//                cap (max_usage), writes site_service_plan_access.
//       Roles  → drop a service to grant it (user_rights = Yes); ✕ revokes.
// Native HTML5 DnD — no library, matching the rest of the app.
// =============================================

interface ServiceRow { service_id: number; service_name: string; parent_service_id: number | null; }
interface PlanRow { plan_id: number; plan_name: string; monthly_price: number; }
interface RoleRow { user_type_id: number; user_type: string; hierarchy_level: number; }

const LEVELS = ['none', 'read', 'limited', 'full'] as const;
type Level = typeof LEVELS[number];
const LEVEL_LABEL: Record<Level, string> = { none: '—', read: 'Read', limited: 'Limited', full: 'Full' };
const SETTABLE: Level[] = ['read', 'limited', 'full'];

interface ServiceBoardProps {
  topLevel: ServiceRow[];
  childrenOf: (id: number) => ServiceRow[];
  activePlans: PlanRow[];
  levelOf: (planId: number, serviceId: number) => Level;
  maxUsageOf: (planId: number, serviceId: number) => number | null;
  setPlanLevel: (planId: number, serviceId: number, next: Level, maxUsage?: number | null) => void;
  activeRoles: RoleRow[];
  roleHas: (typeId: number, serviceId: number, serviceName: string) => boolean;
  toggleRole: (typeId: number, serviceId: number, serviceName: string) => void;
  reparentService: (serviceId: number, newParentId: number | null) => Promise<boolean>;
  busyKey: string;
}

interface PopState { planId: number; serviceId: number; serviceName: string; level: Level; maxUsage: string; }

const ServiceBoard: React.FC<ServiceBoardProps> = ({
  topLevel, childrenOf, activePlans, levelOf, maxUsageOf, setPlanLevel,
  activeRoles, roleHas, toggleRole, reparentService, busyKey,
}) => {
  const [colType, setColType] = useState<'plans' | 'roles'>('plans');
  const [dragId, setDragId] = useState<number | null>(null);
  const [overKey, setOverKey] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pop, setPop] = useState<PopState | null>(null);
  const toggleExpand = useCallback((id: number) => {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  // Open the whole tree once data arrives (sub-services + modules visible by default).
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current || !topLevel.length) return;
    didInit.current = true;
    const all = new Set<number>();
    const walk = (list: ServiceRow[]) => list.forEach(s => { const k = childrenOf(s.service_id); if (k.length) { all.add(s.service_id); walk(k); } });
    walk(topLevel);
    setExpanded(all);
  }, [topLevel, childrenOf]);

  const nameOf = useCallback((id: number): string => {
    const find = (list: ServiceRow[]): string | null => {
      for (const s of list) { if (s.service_id === id) return s.service_name; const c = find(childrenOf(s.service_id)); if (c) return c; }
      return null;
    };
    return find(topLevel) || `#${id}`;
  }, [topLevel, childrenOf]);

  const onDragStart = (e: React.DragEvent, id: number) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(id)); } catch { /* some browsers require this in a try */ }
  };
  const onDragEnd = () => { setDragId(null); setOverKey(''); };
  const allow = (e: React.DragEvent, key: string) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overKey !== key) setOverKey(key); };

  const dropOnService = (e: React.DragEvent, targetId: number) => {
    e.preventDefault(); setOverKey('');
    if (dragId == null || dragId === targetId) return;
    reparentService(dragId, targetId);
  };
  const dropOnRoot = (e: React.DragEvent) => {
    e.preventDefault(); setOverKey('');
    if (dragId != null) reparentService(dragId, null);
  };
  const dropOnPlan = (e: React.DragEvent, planId: number) => {
    e.preventDefault(); setOverKey('');
    if (dragId == null) return;
    openPop(planId, dragId);
  };
  const dropOnRole = (e: React.DragEvent, typeId: number) => {
    e.preventDefault(); setOverKey('');
    if (dragId == null) return;
    if (!roleHas(typeId, dragId, nameOf(dragId))) toggleRole(typeId, dragId, nameOf(dragId));
  };

  const openPop = (planId: number, serviceId: number) => {
    const cur = levelOf(planId, serviceId);
    const usage = maxUsageOf(planId, serviceId);
    setPop({ planId, serviceId, serviceName: nameOf(serviceId), level: cur === 'none' ? 'full' : cur, maxUsage: usage == null ? '' : String(usage) });
  };
  const savePop = () => {
    if (!pop) return;
    const usage = pop.maxUsage.trim() === '' ? null : Math.max(0, Math.round(Number(pop.maxUsage) || 0));
    setPlanLevel(pop.planId, pop.serviceId, pop.level, usage);
    setPop(null);
  };

  // ---- service tree (drag source + reparent targets) ----
  const renderNode = (svc: ServiceRow, depth: number) => {
    const kids = childrenOf(svc.service_id);
    const isParent = kids.length > 0;
    const isOpen = expanded.has(svc.service_id);
    const key = `svc${svc.service_id}`;
    return (
      <React.Fragment key={svc.service_id}>
        <div
          className={`sb-node ${overKey === key ? 'sb-over' : ''} ${dragId === svc.service_id ? 'sb-dragging' : ''} ${busyKey === `mv${svc.service_id}` ? 'sb-busy' : ''}`}
          style={{ marginLeft: depth * 18 }}
          draggable
          onDragStart={e => onDragStart(e, svc.service_id)}
          onDragEnd={onDragEnd}
          onDragOver={e => allow(e, key)}
          onDragLeave={() => setOverKey(k => (k === key ? '' : k))}
          onDrop={e => dropOnService(e, svc.service_id)}
          title="Drag onto another service to re-parent, or into a column"
        >
          {isParent
            ? <button className="sb-expand" onClick={() => toggleExpand(svc.service_id)} aria-label={isOpen ? 'Collapse' : 'Expand'}>{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</button>
            : <span className="sb-expand-spacer" />}
          <FiMove className="sb-grip" size={13} />
          <span className="sb-node-name">{svc.service_name}</span>
          {isParent && <span className="sb-count">{kids.length}</span>}
        </div>
        {isParent && isOpen && kids.map(k => renderNode(k, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="sb">
      <div className="sb-pane sb-tree-pane">
        <div className="sb-pane-head"><FiMove size={13} /> Services <span className="sb-pane-sub">drag to re-parent</span></div>
        <div
          className={`sb-root-zone ${overKey === 'root' ? 'sb-over' : ''}`}
          onDragOver={e => allow(e, 'root')}
          onDragLeave={() => setOverKey(k => (k === 'root' ? '' : k))}
          onDrop={dropOnRoot}
        >
          <FiCornerUpLeft size={12} /> Drop here to make top-level
        </div>
        <div className="sb-tree">{topLevel.map(s => renderNode(s, 0))}</div>
      </div>

      <div className="sb-pane sb-plans-pane">
        <div className="sb-pane-head">
          {colType === 'plans' ? <FiTag size={13} /> : <FiShield size={13} />}
          {colType === 'plans' ? 'Plans' : 'Roles'}
          <span className="sb-pane-sub">drop a service to {colType === 'plans' ? 'include it' : 'grant it'}</span>
          <div className="sb-coltype" role="tablist">
            <button role="tab" aria-selected={colType === 'plans'} className={`sb-coltype-btn ${colType === 'plans' ? 'active' : ''}`} onClick={() => { setColType('plans'); setPop(null); }}><FiTag size={12} /> Plans</button>
            <button role="tab" aria-selected={colType === 'roles'} className={`sb-coltype-btn ${colType === 'roles' ? 'active' : ''}`} onClick={() => { setColType('roles'); setPop(null); }}><FiShield size={12} /> Roles</button>
          </div>
        </div>

        {colType === 'plans' && (
          <div className="sb-plan-cols">
            {activePlans.map(p => {
              const included = collectIncludedPlans(topLevel, childrenOf, sid => levelOf(p.plan_id, sid), sid => maxUsageOf(p.plan_id, sid));
              const key = `plan${p.plan_id}`;
              const popHere = pop && pop.planId === p.plan_id;
              return (
                <div
                  key={p.plan_id}
                  className={`sb-plan-col ${overKey === key ? 'sb-over' : ''}`}
                  onDragOver={e => allow(e, key)}
                  onDragLeave={() => setOverKey(k => (k === key ? '' : k))}
                  onDrop={e => dropOnPlan(e, p.plan_id)}
                >
                  <div className="sb-plan-head">
                    <span className="sb-plan-name">{p.plan_name}</span>
                    <span className="sb-plan-meta">{Number(p.monthly_price) === 0 ? 'Free' : `$${Number(p.monthly_price).toFixed(2)}/mo`}</span>
                  </div>
                  <div className="sb-plan-body">
                    {popHere && (
                      <div className="sb-pop">
                        <div className="sb-pop-title">{pop!.serviceName}</div>
                        <div className="sb-pop-seg">
                          {SETTABLE.map(l => (
                            <button key={l} className={`sb-pop-seg-btn ${pop!.level === l ? `on sb-lvl-${l}` : ''}`} onClick={() => setPop(s => s && { ...s, level: l })}>{LEVEL_LABEL[l]}</button>
                          ))}
                        </div>
                        <label className="sb-pop-usage">
                          <span>Max items</span>
                          <input type="number" min={0} placeholder="∞" value={pop!.maxUsage} onChange={e => setPop(s => s && { ...s, maxUsage: e.target.value })} />
                        </label>
                        <div className="sb-pop-actions">
                          <button className="sb-pop-save" onClick={savePop}>Save</button>
                          <button className="sb-pop-cancel" onClick={() => setPop(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                    {included.length === 0 && !popHere && <div className="sb-plan-empty">Drag services here</div>}
                    {included.map(({ id, level, maxUsage }) => (
                      <div key={id} className={`sb-pill sb-lvl-${level}`}>
                        <span className="sb-pill-name">{nameOf(id)}</span>
                        {maxUsage != null && <span className="sb-pill-cap" title="Item cap">×{maxUsage}</span>}
                        <button className="sb-pill-lvl" onClick={() => openPop(p.plan_id, id)} title="Edit access level / item cap">{LEVEL_LABEL[level]}</button>
                        <button className="sb-pill-x" onClick={() => setPlanLevel(p.plan_id, id, 'none')} title="Remove from plan" aria-label="Remove"><FiX size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {colType === 'roles' && (
          <div className="sb-plan-cols">
            {activeRoles.map(r => {
              const included = collectIncludedRoles(topLevel, childrenOf, sid => roleHas(r.user_type_id, sid, nameOf(sid)));
              const key = `role${r.user_type_id}`;
              return (
                <div
                  key={r.user_type_id}
                  className={`sb-plan-col ${overKey === key ? 'sb-over' : ''}`}
                  onDragOver={e => allow(e, key)}
                  onDragLeave={() => setOverKey(k => (k === key ? '' : k))}
                  onDrop={e => dropOnRole(e, r.user_type_id)}
                >
                  <div className="sb-plan-head">
                    <span className="sb-plan-name">{r.user_type}</span>
                    <span className="sb-plan-meta">level {r.hierarchy_level}</span>
                  </div>
                  <div className="sb-plan-body">
                    {included.length === 0 && <div className="sb-plan-empty">Drag services here</div>}
                    {included.map(id => (
                      <div key={id} className="sb-pill sb-pill-role">
                        <FiCheck size={12} />
                        <span className="sb-pill-name">{nameOf(id)}</span>
                        <button className="sb-pill-x" onClick={() => toggleRole(r.user_type_id, id, nameOf(id))} title="Revoke from role" aria-label="Revoke"><FiX size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

function collectIncludedPlans(
  topLevel: ServiceRow[],
  childrenOf: (id: number) => ServiceRow[],
  levelFor: (serviceId: number) => Level,
  usageFor: (serviceId: number) => number | null,
): { id: number; level: Level; maxUsage: number | null }[] {
  const out: { id: number; level: Level; maxUsage: number | null }[] = [];
  const walk = (list: ServiceRow[]) => list.forEach(s => {
    const lvl = levelFor(s.service_id);
    if (lvl !== 'none') out.push({ id: s.service_id, level: lvl, maxUsage: usageFor(s.service_id) });
    walk(childrenOf(s.service_id));
  });
  walk(topLevel);
  return out;
}

function collectIncludedRoles(
  topLevel: ServiceRow[],
  childrenOf: (id: number) => ServiceRow[],
  has: (serviceId: number) => boolean,
): number[] {
  const out: number[] = [];
  const walk = (list: ServiceRow[]) => list.forEach(s => { if (has(s.service_id)) out.push(s.service_id); walk(childrenOf(s.service_id)); });
  walk(topLevel);
  return out;
}

export default ServiceBoard;
